import dayjs from 'dayjs';

// Excel date conversion utility
// Excel stores dates as number of days since 1900-01-01
export const excelDateToJS = (excelDate) => {
  // Excel epoch starts at 1900-01-01 but has a leap year bug
  // It considers 1900 as a leap year (which it's not)
  const excelEpoch = new Date(1900, 0, 1);
  const offsetDays = excelDate - 2; // Adjust for Excel's leap year bug
  const jsDate = new Date(excelEpoch.getTime() + offsetDays * 24 * 60 * 60 * 1000);
  return jsDate;
};

// Function to convert raw attendee data to session summaries
export const transformSessionData = (rawData) => {
  if (!Array.isArray(rawData) || rawData.length === 0) {
    return [];
  }

  // Group by SessionID to aggregate data
  const sessionGroups = rawData.reduce((acc, row) => {
    const sessionId = row.SessionID;
    if (!acc[sessionId]) {
      acc[sessionId] = {
        id: sessionId,
        sessionId: sessionId,
        sessionName: row.SessionName,
        sessionDate: row.SessionDate,
        sessionDescription: row.SessionDiscription,
        targetedSkills: row.TargetedSkill,
        seatsAlloted: row.SeatsAlloted,
        sessionStatus: row.SessionStatus,
        status: row.Status,
        hosts: new Set(),
        attendees: []
      };
    }
    
    // Add host to the set (will automatically deduplicate)
    if (row.SessionHostName) {
      acc[sessionId].hosts.add(row.SessionHostName);
    }
    
    // Add attendee information (deduplicate by employeeId within each session)
    if (row.AttendeeEmployeeID) {
      const existingAttendee = acc[sessionId].attendees.find(
        a => a.employeeId === row.AttendeeEmployeeID
      );
      
      if (!existingAttendee) {
        acc[sessionId].attendees.push({
          employeeId: row.AttendeeEmployeeID,
          name: row.AttendeeName,
          email: row.AttendeeEmail,
          attendanceStatus: row.AttendanceStatus,
          feedbackSubmitted: row.FeedbackSubmitted,
          averageRating: row.AverageRating
        });
      }
    }
    
    return acc;
  }, {});

  // Convert grouped data to final format
  const sessionSummaries = Object.values(sessionGroups).map(session => {
    const presentCount = session.attendees.filter(a => a.attendanceStatus === 'Present').length;
    const registeredCount = session.attendees.length;
    const feedbackSubmittedCount = session.attendees.filter(a => a.feedbackSubmitted === 'Y').length;
    const noShowCount = session.attendees.filter(a => a.attendanceStatus === 'NULL').length;
    
    // Calculate average rating for attendees who have ratings (regardless of attendance/feedback status)
    const attendeesWithRatings = session.attendees.filter(a => {
      const rating = parseFloat(a.averageRating);
      return rating && rating > 0;
    });
    const totalRatings = attendeesWithRatings.reduce((sum, a) => sum + parseFloat(a.averageRating), 0);
    const averageRating = attendeesWithRatings.length > 0 ? (totalRatings / attendeesWithRatings.length).toFixed(1) : null;
    
    // Convert Excel date to JavaScript Date
    const sessionDate = excelDateToJS(session.sessionDate);
    
    return {
      id: session.id,
      sessionId: session.sessionId,
      sessionName: session.sessionName,
      sessionDate: dayjs(sessionDate).format('YYYY-MM-DD'),
      sessionDescription: session.sessionDescription,
      targetedSkills: session.targetedSkills,
      seatsAlloted: session.seatsAlloted,
      sessionStatus: session.sessionStatus,
      status: session.status,
      hosts: Array.from(session.hosts), // Convert Set to Array
      hostNames: Array.from(session.hosts).join(', '),
      
      // Attendance metrics
      totalSeats: session.seatsAlloted,
      registeredParticipants: registeredCount,
      actualAttendees: presentCount,
      noShows: noShowCount,
      attendanceRate: registeredCount > 0 ? ((presentCount / registeredCount) * 100).toFixed(1) : 0,
      
      // Feedback metrics
      feedbackSubmitted: feedbackSubmittedCount > 0 ? 'Yes' : 'No',
      feedbackSubmittedCount: feedbackSubmittedCount,
      feedbackSubmissionRate: presentCount > 0 ? ((feedbackSubmittedCount / presentCount) * 100).toFixed(1) : 0,
      
      // Rating metrics
      averageRating: averageRating,
      ratingCount: attendeesWithRatings.length,
      
      // Detailed attendee list
      attendees: session.attendees
    };
  });

  // Sort by date (newest first)
  return sessionSummaries.sort((a, b) => dayjs(b.sessionDate).valueOf() - dayjs(a.sessionDate).valueOf());
};

// Function to get unique values for filters
export const getUniqueValues = (sessions, field) => {
  const values = new Set();
  
  sessions.forEach(session => {
    if (field === 'hosts') {
      session.hosts.forEach(host => values.add(host));
    } else if (field === 'targetedSkills') {
      // Split skills by comma and clean them
      session.targetedSkills.split(',').forEach(skill => {
        const cleanSkill = skill.trim();
        if (cleanSkill) values.add(cleanSkill);
      });
    } else if (session[field]) {
      values.add(session[field]);
    }
  });
  
  return Array.from(values).sort();
};

// Function to calculate summary statistics
export const calculateSummaryStats = (sessions) => {
  const totalSessions = sessions.length;
  const completedSessions = sessions.filter(s => s.sessionStatus === 'Completed').length;
  const publishedSessions = sessions.filter(s => s.sessionStatus === 'Published').length;
  const draftSessions = sessions.filter(s => s.sessionStatus === 'Draft').length;
  
  const totalSeats = sessions.reduce((sum, s) => sum + s.totalSeats, 0);
  const totalRegistered = sessions.reduce((sum, s) => sum + s.registeredParticipants, 0);
  const totalAttendees = sessions.reduce((sum, s) => sum + s.actualAttendees, 0);
  const totalFeedbackSubmitted = sessions.reduce((sum, s) => sum + s.feedbackSubmittedCount, 0);
  
  const avgAttendanceRate = totalRegistered > 0 ? ((totalAttendees / totalRegistered) * 100).toFixed(1) : 0;
  const avgFeedbackRate = totalAttendees > 0 ? ((totalFeedbackSubmitted / totalAttendees) * 100).toFixed(1) : 0;
  
  return {
    totalSessions,
    completedSessions,
    publishedSessions,
    draftSessions,
    totalSeats,
    totalRegistered,
    totalAttendees,
    totalFeedbackSubmitted,
    avgAttendanceRate,
    avgFeedbackRate
  };
};

// Function to load session data from JSON file (for development)
export const loadSessionDataFromJSON = async () => {
  try {
    const response = await fetch('/session-data.json');
    if (!response.ok) {
      throw new Error('Failed to load session data');
    }
    const rawData = await response.json();
    return transformSessionData(rawData);
  } catch (error) {
    console.error('Error loading session data:', error);
    return [];
  }
};

// Function to load session data from API

// Function to transform API response data
export const transformSessionDataFromAPI = (rawData) => {
  if (!Array.isArray(rawData) || rawData.length === 0) {
    return [];
  }

  // Group by SessionID to aggregate data
  const sessionGroups = rawData.reduce((acc, row) => {
    const sessionId = row.sessionID;
    if (!acc[sessionId]) {
      acc[sessionId] = {
        id: sessionId,
        sessionId: sessionId,
        sessionName: row.sessionName,
        sessionDate: row.sessionDate,
        sessionDescription: row.sessionDiscription,
        targetedSkills: row.targetedSkill,
        seatsAlloted: row.seatsAlloted,
        sessionStatus: row.sessionStatus,
        status: row.status,
        hosts: new Set(),
        attendees: []
      };
    }
    
    // Add host to the set (will automatically deduplicate)
    if (row.sessionHostName) {
      // Split multiple hosts by comma and clean them
      row.sessionHostName.split(',').forEach(host => {
        const cleanHost = host.trim();
        if (cleanHost) acc[sessionId].hosts.add(cleanHost);
      });
    }
    
    // Add attendee information (deduplicate by employeeId within each session)
    if (row.attendeeEmployeeID) {
      const existingAttendee = acc[sessionId].attendees.find(
        a => a.employeeId === row.attendeeEmployeeID
      );
      
      if (!existingAttendee) {
        acc[sessionId].attendees.push({
          employeeId: row.attendeeEmployeeID,
          name: row.attendeeName,
          email: row.attendeeEmail,
          attendanceStatus: row.attendanceStatus,
          feedbackSubmitted: row.feedbackSubmitted,
          averageRating: row.AverageRating || row.averageRating || 0 // Handle both casings
        });
      }
    }
    
    return acc;
  }, {});

  // Convert grouped data to final format
  const sessionSummaries = Object.values(sessionGroups).map(session => {
    const presentCount = session.attendees.filter(a => a.attendanceStatus === 'Present').length;
    const registeredCount = session.attendees.length;
    const feedbackSubmittedCount = session.attendees.filter(a => a.feedbackSubmitted === 'Y').length;
    const noShowCount = session.attendees.filter(a => a.attendanceStatus === 'NULL' || a.attendanceStatus === 'Absent').length;
    
    // Calculate average rating for attendees who have ratings (regardless of attendance/feedback status)
    const attendeesWithRatings = session.attendees.filter(a => {
      const rating = parseFloat(a.averageRating);
      return rating && rating > 0;
    });
    const totalRatings = attendeesWithRatings.reduce((sum, a) => sum + parseFloat(a.averageRating), 0);
    const averageRating = attendeesWithRatings.length > 0 ? (totalRatings / attendeesWithRatings.length).toFixed(1) : null;
    
    // Parse the date from API format (2024-05-01T00:00:00)
    const sessionDate = new Date(session.sessionDate);
    
    return {
      id: session.id,
      sessionId: session.sessionId,
      sessionName: session.sessionName,
      sessionDate: dayjs(sessionDate).format('YYYY-MM-DD'),
      sessionDescription: session.sessionDescription,
      targetedSkills: session.targetedSkills,
      seatsAlloted: session.seatsAlloted,
      sessionStatus: session.sessionStatus,
      status: session.status,
      hosts: Array.from(session.hosts), // Convert Set to Array
      hostNames: Array.from(session.hosts).join(', '),
      
      // Attendance metrics
      totalSeats: session.seatsAlloted,
      registeredParticipants: registeredCount,
      actualAttendees: presentCount,
      noShows: noShowCount,
      attendanceRate: registeredCount > 0 ? ((presentCount / registeredCount) * 100).toFixed(1) : 0,
      
      // Feedback metrics
      feedbackSubmitted: feedbackSubmittedCount > 0 ? 'Yes' : 'No',
      feedbackSubmittedCount: feedbackSubmittedCount,
      feedbackSubmissionRate: presentCount > 0 ? ((feedbackSubmittedCount / presentCount) * 100).toFixed(1) : 0,
      
      // Rating metrics
      averageRating: averageRating,
      ratingCount: attendeesWithRatings.length,
      
      // Detailed attendee list
      attendees: session.attendees
    };
  });

  // Sort by date (newest first)
  return sessionSummaries.sort((a, b) => dayjs(b.sessionDate).valueOf() - dayjs(a.sessionDate).valueOf());
};